import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  GridRowId,
  GridRowParams,
  GridRowSelectionModel,
} from "@mui/x-data-grid-premium";
import { useSnackbar } from "../../contexts/SnackbarContext";
import {
  AI_HUB_MAX_ROW_SELECTION,
  aiHubRowSelectionLimitMessage,
} from "./constants";

type UseCrossPageRowSelectionOptions = {
  maxSelection?: number;
};

type RowSelectionChangeDetails = {
  reason?: "singleRowSelection" | "multipleRowsSelection";
};

function createEmptySelection(): GridRowSelectionModel {
  return { type: "include", ids: new Set() };
}

function isEmptyInclude(model: GridRowSelectionModel): boolean {
  return model.type === "include" && model.ids.size === 0;
}

function isPageSelectionSaturated<T extends { id: string }>(
  rows: readonly T[],
  prev: Readonly<Record<string, T>>,
  maxSelection: number,
): boolean {
  if (!Number.isFinite(maxSelection)) return false;
  const selectedOnPage = rows.filter((row) => prev[row.id]).length;
  if (selectedOnPage === 0) return false;
  const otherSelected = Object.keys(prev).length - selectedOnPage;
  const remainingSlots = Math.max(0, maxSelection - otherSelected);
  return selectedOnPage >= Math.min(rows.length, remainingSlots);
}

function clearCurrentPageSelection<T extends { id: string }>(
  prev: Readonly<Record<string, T>>,
  rows: readonly T[],
): {
  model: GridRowSelectionModel;
  selectedRows: Record<string, T>;
  limited: boolean;
} {
  const selectedRows: Record<string, T> = { ...prev };
  for (const row of rows) {
    delete selectedRows[row.id];
  }
  return {
    model: { type: "include", ids: new Set(Object.keys(selectedRows)) },
    selectedRows,
    limited: false,
  };
}

function syncSelectedRows<T extends { id: string }>(
  model: GridRowSelectionModel,
  rows: readonly T[],
  prev: Readonly<Record<string, T>>,
): Record<string, T> {
  const next: Record<string, T> = {};

  if (model.type === "exclude") {
    for (const [id, row] of Object.entries(prev)) {
      if (!model.ids.has(id)) next[id] = row;
    }
    for (const row of rows) {
      if (!model.ids.has(row.id)) next[row.id] = row;
    }
    return next;
  }

  for (const id of model.ids) {
    const key = String(id);
    const row = rows.find((item) => item.id === key) ?? prev[key];
    if (row) next[key] = row;
  }
  return next;
}

function takeLimitedIds(
  candidateIds: readonly string[],
  previousIds: ReadonlySet<string>,
  maxSelection: number,
): string[] {
  const kept: string[] = [];
  for (const id of candidateIds) {
    if (previousIds.has(id)) kept.push(id);
    if (kept.length >= maxSelection) return kept;
  }
  for (const id of candidateIds) {
    if (!previousIds.has(id)) kept.push(id);
    if (kept.length >= maxSelection) return kept;
  }
  return kept;
}

function resolveLimitedSelection<T extends { id: string }>(
  model: GridRowSelectionModel,
  rows: readonly T[],
  prev: Readonly<Record<string, T>>,
  maxSelection: number,
): {
  model: GridRowSelectionModel;
  selectedRows: Record<string, T>;
  limited: boolean;
} {
  const synced = syncSelectedRows(model, rows, prev);
  const candidateIds = Object.keys(synced);
  const limited =
    Number.isFinite(maxSelection) && candidateIds.length > maxSelection;
  const keptIds = limited
    ? takeLimitedIds(candidateIds, new Set(Object.keys(prev)), maxSelection)
    : candidateIds;

  const selectedRows: Record<string, T> = {};
  for (const id of keptIds) {
    selectedRows[id] = synced[id];
  }

  return {
    model: { type: "include", ids: new Set(keptIds) },
    selectedRows,
    limited,
  };
}

function isSelectedId(
  selectedRowsById: Readonly<Record<string, unknown>>,
  id: GridRowId,
): boolean {
  return Boolean(selectedRowsById[String(id)]);
}

export function useCrossPageRowSelection<T extends { id: string }>(
  rows: readonly T[],
  resetKey: string,
  options?: UseCrossPageRowSelectionOptions,
) {
  const { showMessage } = useSnackbar();
  const maxSelection = options?.maxSelection ?? AI_HUB_MAX_ROW_SELECTION;
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>(
    createEmptySelection,
  );
  const [selectedRowsById, setSelectedRowsById] = useState<
    Readonly<Record<string, T>>
  >({});
  const selectedRowsByIdRef = useRef(selectedRowsById);
  selectedRowsByIdRef.current = selectedRowsById;

  useEffect(() => {
    setSelectionModel(createEmptySelection());
    setSelectedRowsById({});
  }, [resetKey]);

  const onRowSelectionModelChange = useCallback(
    (model: GridRowSelectionModel, details?: RowSelectionChangeDetails) => {
      const prev = selectedRowsByIdRef.current;
      const isHeaderToggle = details?.reason === "multipleRowsSelection";
      const resolved =
        isHeaderToggle &&
        (isEmptyInclude(model) ||
          isPageSelectionSaturated(rows, prev, maxSelection))
          ? clearCurrentPageSelection(prev, rows)
          : resolveLimitedSelection(model, rows, prev, maxSelection);

      setSelectionModel(resolved.model);
      setSelectedRowsById(resolved.selectedRows);
      if (resolved.limited) {
        showMessage(aiHubRowSelectionLimitMessage(maxSelection), "warning");
      }
    },
    [maxSelection, rows, showMessage],
  );

  const isRowSelectable = useCallback(
    (params: Pick<GridRowParams, "id">) => {
      if (!Number.isFinite(maxSelection)) return true;
      if (isSelectedId(selectedRowsById, params.id)) return true;
      return Object.keys(selectedRowsById).length < maxSelection;
    },
    [maxSelection, selectedRowsById],
  );

  const selectedRows = useMemo(
    () => Object.values(selectedRowsById),
    [selectedRowsById],
  );

  return {
    selectionModel,
    onRowSelectionModelChange,
    isRowSelectable,
    selectedRows,
    hasSelection: selectedRows.length > 0,
  };
}
